import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type CreditBalance } from '../../lib/api';
import { formatCredits } from '@/lib/format-credits';
import { useAuth } from '../../auth/hooks/useAuth';

/**
 * Compact credit balance indicator for the navigation header.
 *
 * Subscription users have a "behind the scenes" experience: the indicator
 * is hidden entirely. If a subscriber runs out mid-cycle, that state is
 * surfaced at the point of failure (402 toast + upsell), not in the header.
 *
 * Free users always see the indicator with their spendable balance.
 */
export function CreditBalanceIndicator() {
  const [balanceData, setBalanceData] = useState<CreditBalance | null>(null);
  const navigate = useNavigate();
  const { isGuest } = useAuth();

  useEffect(() => {
    const load = () => {
      api
        .getCreditBalance()
        .then(setBalanceData)
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Guests get the GuestTrialBanner (system-bucket trial balance) instead — the
  // spendable metric here excludes the system bucket, so a guest would
  // misleadingly see "F 0". Checked after the hooks to respect rules-of-hooks.
  if (isGuest || !balanceData) return null;

  const { buckets, subscription } = balanceData;
  const onSub = subscription.isOnSubscription;

  // Subscribers: fully hidden. Even when the monthly allocation is spent,
  // we don't surface it here — the 402 flow at the call site handles that.
  if (onSub) {
    return null;
  }

  // Free users: show spendable (Fias + any leftover subscription bucket).
  // System credits are never shown here by design.
  const spendableBalance = buckets.subscription + buckets.fias;
  const isLow = spendableBalance < 50;
  const displayAmount = formatCredits(Math.round(spendableBalance));

  const tooltip = 'Credit balance — click to manage';

  return (
    <button
      onClick={() => navigate('/credits')}
      title={tooltip}
      className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
        isLow
          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
          : 'bg-muted text-muted-foreground hover:bg-accent'
      }`}
    >
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        <text
          x="12"
          y="16.5"
          textAnchor="middle"
          fill="currentColor"
          fontSize="12"
          fontWeight="bold"
          fontFamily="sans-serif"
        >
          F
        </text>
      </svg>
      {displayAmount}
    </button>
  );
}
